import {
  Outlet,
  createRootRouteWithContext,
  useLocation,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AuthShell,
  SENTINEL_DARK_LOGO_URL,
  ThemeProvider,
  Toaster,
  useBranding,
} from '@sentinel/ui';
import { type ApiClient } from '@sentinel/api-client';
import {
  ForbiddenToastListener,
  ImpersonationBanner,
  type ImpersonationBannerMessages,
  useFocusOnRouteChange,
  useSession,
} from '@sentinel/auth-core';
import { BrandingLoader } from '../features/branding/branding-loader';
import { PortalStepUpProvider } from '../features/step-up/provider';
import { PortalFederatedStepUpProvider } from '../features/step-up/federated-provider';
import { useResumeFederatedStepUp } from '../features/step-up/use-resume-federated-step-up';
import '../styles/stylex.css';

export interface RootContext { queryClient: QueryClient; apiClient: ApiClient }

function PortalChrome({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  const branding = useBranding();
  const year = new Date().getFullYear();
  const brandLogoSrc = branding.logoUrl ?? SENTINEL_DARK_LOGO_URL;
  const productName = branding.productName?.toUpperCase() ?? 'SENTINEL';
  return (
    <AuthShell
      brandWordmark={<img src={brandLogoSrc} alt={productName} height={48} />}
      footer={
        <span>
          © {year} VicOne · <span>{t('footer.tagline')}</span>
        </span>
      }
    >
      {children}
    </AuthShell>
  );
}

/**
 * Listens for `sentinel:unauthenticated` and navigates to `/login` in-SPA.
 * Belt-and-braces for routes outside `/account/*` (Family 1J's AccountLayout
 * handles the in-/account/* case via a session-state-change effect; this
 * fires for protected routes that don't sit under AccountLayout).
 *
 * Gated on `useSession().user` being non-null: unauthenticated surfaces
 * (/logout, /mfa/setup, /magic-link, /reset-password, etc.) legitimately
 * receive 401s from their own flows and must not be redirected. Registering
 * the window listener only when a session exists also makes us robust against
 * future endpoints we might forget to opt out — no session to lose, no-op.
 *
 * Latch prevents double-navigation on rapid 401s; one event triggers exactly
 * one navigate during the page's lifetime, after which the next mount resets.
 */
function UnauthRedirectListener() {
  const navigate = useNavigate();
  const { user } = useSession();
  const fired = useRef(false);
  useEffect(() => {
    if (!user) return;
    function onUnauth() {
      if (fired.current) return;
      if (window.location.pathname.startsWith('/login')) return;
      fired.current = true;
      void navigate({ to: '/login', replace: true });
    }
    window.addEventListener('sentinel:unauthenticated', onUnauth);
    return () => window.removeEventListener('sentinel:unauthenticated', onUnauth);
  }, [navigate, user]);
  return null;
}

function RootShell() {
  const { apiClient } = useRouteContext({ from: '__root__' });
  const location = useLocation();
  useFocusOnRouteChange();
  useResumeFederatedStepUp();
  const { t } = useTranslation('impersonation');
  const bannerMessages = useMemo<ImpersonationBannerMessages>(
    () => ({
      impersonating: t('label'),
      stop: t('stop'),
      stopping: t('stopping'),
      failure: t('failure'),
    }),
    [t],
  );
  const isAppRoute = location.pathname === '/account' || location.pathname.startsWith('/account/');
  return (
    <ThemeProvider>
      <BrandingLoader>
        <PortalStepUpProvider apiClient={apiClient}>
          <PortalFederatedStepUpProvider apiClient={apiClient}>
            {/* Portal had no toast surface before. The global permission-denied
                handler lives outside the component tree, so it has no inline slot
                to write into — portal's `feedback` is a SectionFeedback prop, not
                a context. Sonner renders its <section> landmark even with zero
                toasts, so the label ships on every route — pass a localized one.
                `t` is the impersonation namespace's; the `common:` prefix crosses
                to the shared namespace. */}
            <Toaster containerAriaLabel={t('common:notifications.region_label')} />
            <UnauthRedirectListener />
            <ForbiddenToastListener message={t('common:errors.forbidden')} />
            <ImpersonationBanner
              apiClient={apiClient}
              messages={bannerMessages}
              stopPath="/system/impersonate/self-stop"
            />
            {isAppRoute ? <Outlet /> : <PortalChrome><Outlet /></PortalChrome>}
          </PortalFederatedStepUpProvider>
        </PortalStepUpProvider>
      </BrandingLoader>
    </ThemeProvider>
  );
}

export const Route = createRootRouteWithContext<RootContext>()({
  component: RootShell,
});
